import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, rupee } from "../lib/api";
import { useStores } from "../lib/stores";
import {
  formatReceiptDate,
  paymentStatusLabel,
  publicInvoiceUrl,
  todayInputValue,
  whatsAppShareUrl,
} from "../lib/saleInvoice";
import TaxInvoiceView from "../components/TaxInvoiceView";
import SaleReturnView from "../components/SaleReturnView";
import EditPaymentReceiptModal from "../components/EditPaymentReceiptModal";
import { ArrowLeft, MessageCircle, Pencil, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function SaleInvoice() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { stores } = useStores();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [returning, setReturning] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [returnQty, setReturnQty] = useState({});
  const [returnNotes, setReturnNotes] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [payForm, setPayForm] = useState({
    amount: "",
    payment_method: "cash",
    received_at: todayInputValue(),
    notes: "",
  });
  const collectRef = useRef(null);
  const [printReturn, setPrintReturn] = useState(null);
  const [editReceipt, setEditReceipt] = useState(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get(`/sales/${id}`)
      .then((r) => {
        setSale(r.data);
        const init = {};
        (r.data.items || []).forEach((it) => {
          const max = Math.max(0, (it.quantity || 0) - (it.returned_quantity || 0));
          if (max > 0) init[it.product_id] = max;
        });
        setReturnQty(init);
      })
      .catch(() => toast.error("Invoice not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const clearPrint = () => setPrintReturn(null);
    window.addEventListener("afterprint", clearPrint);
    return () => window.removeEventListener("afterprint", clearPrint);
  }, []);

  const handlePrintReturn = (ret) => {
    setPrintReturn(ret);
    setTimeout(() => window.print(), 200);
  };

  useEffect(() => {
    if (searchParams.get("collect") === "1" && collectRef.current) {
      collectRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [sale, searchParams]);

  const store = stores.find((s) => s.id === sale?.store_id) || stores.find((s) => s.is_default) || stores[0];

  useEffect(() => {
    if (sale?.balance_due > 0) {
      setPayForm((f) => ({ ...f, amount: String(sale.balance_due) }));
    }
  }, [sale?.balance_due]);

  const collectPayment = async (e) => {
    e.preventDefault();
    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setCollecting(true);
    try {
      const { data } = await api.post(`/sales/${id}/payments`, {
        amount,
        payment_method: payForm.payment_method,
        received_at: payForm.received_at,
        notes: payForm.notes || undefined,
      });
      setSale(data);
      toast.success("Payment recorded");
      setPayForm((f) => ({ ...f, notes: "", received_at: todayInputValue() }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    } finally {
      setCollecting(false);
    }
  };

  const shareWhatsApp = () => {
    if (!sale?.public_token) return toast.error("Invoice link is not available");
    const link = publicInvoiceUrl(sale.public_token);
    const message = [
      `Tax invoice *${sale.invoice_no}*${store?.name ? ` from ${store.name}` : ""}`,
      "",
      "View & download your invoice:",
      link,
    ].join("\n");
    window.open(whatsAppShareUrl(sale.customer_snapshot?.phone, message), "_blank");
  };

  const canReturn = sale?.items?.some((it) => (it.quantity || 0) - (it.returned_quantity || 0) > 0);
  const canEdit = !(sale?.returns?.length > 0) && sale?.return_status === "active";
  const canEditReceipt = canEdit && (sale?.payments?.length > 0 || (sale?.amount_paid > 0 && sale?.payment_method !== "pending"));

  const openEditReceipt = (payment = null) => {
    setEditReceipt(payment);
    setReceiptModalOpen(true);
  };

  const setReturnItemQty = (productId, value, max) => {
    const n = Math.min(max, Math.max(0, parseInt(value, 10) || 0));
    setReturnQty((s) => ({ ...s, [productId]: n }));
  };

  const submitReturn = async () => {
    const items = Object.entries(returnQty)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (items.length === 0) {
      toast.error("Set return quantity for at least one item");
      return;
    }

    setReturning(true);
    try {
      const { data } = await api.post(`/sales/${id}/returns`, {
        items,
        refund_method: refundMethod,
        notes: returnNotes || undefined,
      });
      toast.success(`Return ${data.return_no} accepted — stock restored`);
      setShowReturn(false);
      setReturnNotes("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Return failed");
    } finally {
      setReturning(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center" style={{ color: "var(--text-muted)" }}>Loading invoice…</div>;
  }
  if (!sale) {
    return (
      <div className="p-8 text-center">
        <p className="mb-4">Invoice not found.</p>
        <Link to="/app/sales" className="text-sm font-semibold" style={{ color: "var(--primary)" }}>← Back to sales</Link>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="sale-invoice-root">
      <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to="/app/sales" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--primary)" }}>
          <ArrowLeft size={16} /> Sales history
        </Link>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Link
              to={`/app/sales/${id}/edit`}
              className="h-10 px-4 rounded-md border font-semibold inline-flex items-center gap-2 tactile-btn bg-white"
              style={{ borderColor: "var(--border-soft)" }}
              data-testid="sale-edit-link"
            >
              <Pencil size={16} /> Edit invoice
            </Link>
          )}
          {canReturn && (
            <button
              type="button"
              onClick={() => setShowReturn((v) => !v)}
              className="h-10 px-4 rounded-md border font-semibold inline-flex items-center gap-2 tactile-btn bg-white"
              style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
            >
              <RotateCcw size={16} /> {showReturn ? "Cancel" : "Customer return"}
            </button>
          )}
          <button
            type="button"
            onClick={shareWhatsApp}
            className="h-10 px-4 rounded-md text-white font-semibold inline-flex items-center gap-2 tactile-btn"
            style={{ background: "#25D366" }}
          >
            <MessageCircle size={16} /> WhatsApp
          </button>
          <button type="button" onClick={() => window.print()} className="h-10 px-4 rounded-md border font-semibold inline-flex items-center gap-2 tactile-btn bg-white" style={{ borderColor: "var(--border-soft)" }}>
            <Printer size={16} /> Print
          </button>
        </div>
      </div>

      <TaxInvoiceView sale={sale} store={store} />

      {(sale.returned_total > 0 || sale.net_total != null) && (
        <div className="mt-4 card-soft p-4 grid sm:grid-cols-3 gap-4 no-print text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Invoice total</div>
            <div className="font-semibold num mt-1">{rupee(sale.total)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Returned (credit)</div>
            <div className="font-semibold num mt-1" style={{ color: "var(--error)" }}>{rupee(sale.returned_total || 0)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Net after returns</div>
            <div className="font-semibold num mt-1">{rupee(sale.net_total ?? sale.total)}</div>
          </div>
        </div>
      )}

      {sale.refund_due > 0 && (
        <div className="mt-4 card-soft p-4 no-print text-sm" style={{ background: "var(--surface-error)" }}>
          <span className="font-semibold" style={{ color: "var(--error)" }}>Refund due to customer: </span>
          <span className="num font-bold" style={{ color: "var(--error)" }}>{rupee(sale.refund_due)}</span>
          <span style={{ color: "var(--text-muted)" }}> — issue cash/UPI/card per return notes below.</span>
        </div>
      )}

      {showReturn && canReturn && (
        <div className="mt-6 card-soft p-5 border-2 no-print" style={{ borderColor: "var(--primary)" }}>
          <h2 className="font-display font-bold text-lg mb-1">Customer return</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            Adjust qty per line (defaults to what’s still returnable). Stock is restored and a credit note is created automatically.
          </p>
          <div className="space-y-3 mb-4">
            {sale.items
              .filter((it) => (it.quantity || 0) - (it.returned_quantity || 0) > 0)
              .map((it) => {
                const max = (it.quantity || 0) - (it.returned_quantity || 0);
                return (
                  <div key={it.product_id} className="flex flex-wrap items-center gap-3 justify-between border rounded-md p-3" style={{ borderColor: "var(--border-soft)" }}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{it.product_name}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                        Sold {it.quantity} · already returned {it.returned_quantity || 0} · max {max}
                      </div>
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={max}
                      value={returnQty[it.product_id] ?? 0}
                      onChange={(e) => setReturnItemQty(it.product_id, e.target.value, max)}
                      className="w-24 h-10 px-2 rounded-md border text-center num"
                      style={{ borderColor: "var(--border-soft)" }}
                    />
                  </div>
                );
              })}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium">Refund method</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["cash", "upi", "card", "adjust_balance"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setRefundMethod(m)}
                    className="h-10 rounded-md text-xs font-semibold border uppercase"
                    style={{
                      borderColor: refundMethod === m ? "var(--primary)" : "var(--border-soft)",
                      background: refundMethod === m ? "var(--drag-highlight)" : "white",
                      color: refundMethod === m ? "var(--primary)" : "inherit",
                    }}
                  >
                    {m.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Reason / notes</label>
              <input
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white"
                style={{ borderColor: "var(--border-soft)" }}
                placeholder="Defect, wrong size, exchange…"
              />
            </div>
          </div>
          <button
            type="button"
            disabled={returning}
            onClick={submitReturn}
            className="h-11 px-8 rounded-md text-white font-semibold tactile-btn"
            style={{ background: "var(--primary)" }}
          >
            {returning ? "Processing…" : "Accept return"}
          </button>
        </div>
      )}

      {sale.returns?.length > 0 && (
        <div className="mt-6 card-soft overflow-hidden no-print">
          <div className="px-4 py-3 border-b font-semibold text-sm" style={{ borderColor: "var(--border-soft)" }}>
            Return history ({sale.returns.length})
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
            {sale.returns.map((ret) => (
              <div key={ret.id} className="px-4 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-mono font-semibold text-sm" style={{ color: "var(--primary)" }}>{ret.return_no}</span>
                    {ret.refund_method && (
                      <span className="ml-2 text-xs uppercase" style={{ color: "var(--text-muted)" }}>{ret.refund_method.replace("_", " ")}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm num font-semibold" style={{ color: "var(--error)" }}>{rupee(ret.total)}</div>
                    <button
                      type="button"
                      onClick={() => handlePrintReturn(ret)}
                      className="h-8 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 tactile-btn bg-white"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <Printer size={14} /> Credit note
                    </button>
                  </div>
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {formatReceiptDate(ret.created_at)}
                  {ret.notes ? ` · ${ret.notes}` : ""}
                </div>
                <ul className="mt-2 text-sm space-y-0.5">
                  {(ret.items || []).map((it) => (
                    <li key={`${ret.id}-${it.product_id}`} className="flex justify-between gap-2">
                      <span className="truncate">{it.product_name} × {it.quantity}</span>
                      <span className="num shrink-0">{rupee(it.line_total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {(sale.payments?.length > 0 || sale.balance_due > 0 || (sale.amount_paid > 0 && sale.payment_method !== "pending")) && (
        <div className="mt-6 grid lg:grid-cols-2 gap-5 no-print">
          {(sale.payments?.length > 0 || (sale.amount_paid > 0 && sale.payment_method !== "pending" && sale.payments?.length === 0)) && (
            <div className="card-soft p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="font-display font-semibold">Payment receipts</div>
                {canEditReceipt && sale.payments?.length === 0 && (
                  <button
                    type="button"
                    onClick={() => openEditReceipt(null)}
                    className="text-xs font-semibold inline-flex items-center gap-1"
                    style={{ color: "var(--primary)" }}
                  >
                    <Pencil size={14} /> Edit receipt
                  </button>
                )}
              </div>
              <div className="space-y-0">
                {sale.payments?.length > 0 ? sale.payments.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-4 py-3"
                    style={{ borderTop: i > 0 ? "1px solid var(--border-soft)" : undefined }}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-sm uppercase">{p.payment_method}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        {formatReceiptDate(p.received_at)}
                        {p.received_by_name ? ` · ${p.received_by_name}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="num font-semibold">{rupee(p.amount)}</div>
                      {canEditReceipt && (
                        <button
                          type="button"
                          onClick={() => openEditReceipt(p)}
                          className="h-8 px-2.5 rounded-md border text-xs font-semibold inline-flex items-center gap-1 bg-white"
                          style={{ borderColor: "var(--border-soft)" }}
                          data-testid={`edit-receipt-${p.id}`}
                        >
                          <Pencil size={13} /> Edit
                        </button>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className="flex items-center justify-between gap-4 py-1">
                    <div>
                      <div className="font-semibold text-sm uppercase">{sale.payment_method}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        {formatReceiptDate(sale.created_at)}
                      </div>
                    </div>
                    <div className="num font-semibold">{rupee(sale.amount_paid || sale.total)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {sale.balance_due > 0 && (
            <div ref={collectRef} className="card-soft p-5">
              <div className="font-display font-semibold mb-1">Collect payment</div>
              <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                Balance due: <span className="font-semibold num" style={{ color: "var(--error)" }}>{rupee(sale.balance_due)}</span>
              </p>
              <form onSubmit={collectPayment} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Amount ₹</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={sale.balance_due}
                    required
                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    className="mt-1 h-11 w-full px-3 rounded-md border bg-white num"
                    style={{ borderColor: "var(--border-soft)" }}
                    data-testid="collect-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Payment mode</label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {["cash", "upi", "card"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPayForm({ ...payForm, payment_method: m })}
                        className="h-10 rounded-md text-sm font-semibold border uppercase"
                        style={{
                          borderColor: payForm.payment_method === m ? "var(--primary)" : "var(--border-soft)",
                          background: payForm.payment_method === m ? "var(--drag-highlight)" : "white",
                          color: payForm.payment_method === m ? "var(--primary)" : "inherit",
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Date of receipt</label>
                  <input
                    type="date"
                    required
                    value={payForm.received_at}
                    onChange={(e) => setPayForm({ ...payForm, received_at: e.target.value })}
                    className="mt-1 h-11 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                    data-testid="collect-date"
                  />
                </div>
                <button
                  type="submit"
                  disabled={collecting}
                  className="w-full h-12 rounded-md text-white font-semibold tactile-btn"
                  style={{ background: "var(--success)" }}
                  data-testid="collect-submit"
                >
                  {collecting ? "Saving…" : "Record payment"}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      <EditPaymentReceiptModal
        open={receiptModalOpen}
        onOpenChange={setReceiptModalOpen}
        sale={sale}
        payment={editReceipt}
        onSaved={setSale}
      />

      <div className={printReturn ? "print-invoice" : "bulk-print-area"}>
        {printReturn && (
          <SaleReturnView sale={sale} saleReturn={printReturn} store={store} />
        )}
      </div>
    </div>
  );
}
